import React, { useCallback, useEffect, useMemo, useReducer } from "react";

import { loginAuthAPI } from "@/auth/api/loginAuthAPI";
import { ActionMapType, AuthStateType, Payload, Types } from "@/auth/types";
import { clearAuthData } from "@/lib/tokenRefresh";

import { AuthContext } from "./authContext";

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

const initialState: AuthStateType = {
  user: null,
  loading: true,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === Types.AUTH_WITH_ONEID) {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      loading: false,
      user: null,
    };
  }
  return state;
};

const STORAGE_KEY = "access_token";

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    const accessToken = localStorage.getItem(STORAGE_KEY);

    if (accessToken) {
      try {
        // Fetch user profile from API
        const profile = await loginAuthAPI.getProfile();

        // Update localStorage with fresh profile data
        localStorage.setItem("user", JSON.stringify(profile));

        const user = {
          id: profile.id,
          full_name: profile.full_name || "",
          avatar: profile.avatar ?? null,
          access_token: accessToken,
          role: profile.role,
          region: profile.region,
          city: profile.city,
          makhalla: profile.makhalla,
        };

        dispatch({ type: Types.AUTH_WITH_ONEID, payload: { user } });
      } catch (error) {
        // If profile fetch fails, try to use cached user data
        const userString = localStorage.getItem("user");
        if (userString) {
          try {
            const userData = JSON.parse(userString);
            const user = {
              id: userData.id,
              full_name: userData.full_name || "",
              avatar: userData.avatar ?? null,
              access_token: accessToken,
              role: userData.role || null,
              region: userData.region ?? null,
              city: userData.city ?? null,
              makhalla: userData.makhalla ?? null,
            };
            dispatch({ type: Types.AUTH_WITH_ONEID, payload: { user } });
          } catch {
            // Clear invalid data
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem("refresh_token");
            localStorage.removeItem("user");
            dispatch({ type: Types.AUTH_WITH_ONEID, payload: { user: null } });
          }
        } else {
          dispatch({ type: Types.AUTH_WITH_ONEID, payload: { user: null } });
        }
      }
    } else {
      dispatch({ type: Types.AUTH_WITH_ONEID, payload: { user: null } });
    }
  }, [dispatch]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const logout = useCallback(async () => {
    try {
      await loginAuthAPI.logout();
    } finally {
      // Clear all authentication data
      clearAuthData();

      // Update context state
      dispatch({
        type: Types.LOGOUT,
      });
    }
  }, [dispatch]);

  const checkAuthenticated = state.user ? "authenticated" : "unauthenticated";
  const status = state.loading ? "loading" : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: "jwt",
      loading: status === "loading",
      authenticated: status === "authenticated",
      unauthenticated: status === "unauthenticated",
      logout,
      dispatch,
    }),
    [logout, state.user, status, dispatch],
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
}
